import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { QAIDA_LESSONS } from "../data/qaida";
import { QAIDA_TABLES, type QaidaTable } from "../data/qaida-tables";
import type { QaidaLesson } from "../types";
import { withBase } from "./base";

// The Qaida content the app is currently teaching from.
//
// The seventeen lessons and ten practice boards compiled into the bundle are the
// default and the fallback. An admin can replace them (see QaidaAdmin), and the
// override is stored in the Worker; this provider is what makes every screen —
// the Qaida pathway, the practice tables, and the live classroom — read the same
// content without each fetching it.
//
// It never blocks a render. The built-in content is shown immediately and the
// override, if there is one, swaps in when it arrives; a slow network delays a
// customisation, never the lesson.

export type QaidaContent = {
  lessons: QaidaLesson[];
  boards: QaidaTable[];
  /** True once an admin has saved their own version. */
  custom: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  loading: boolean;
  /** Re-read after saving from the admin screen. */
  reload: () => void;
};

const BUILT_IN: Pick<QaidaContent, "lessons" | "boards"> = { lessons: QAIDA_LESSONS, boards: QAIDA_TABLES };

const ContentContext = createContext<QaidaContent>({
  ...BUILT_IN,
  custom: false,
  updatedAt: null,
  updatedBy: null,
  loading: false,
  reload: () => {},
});

type ApiResponse = {
  custom?: boolean;
  document?: { lessons?: QaidaLesson[]; boards?: QaidaTable[] } | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export function ContentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<QaidaContent, "reload">>({ ...BUILT_IN, custom: false, updatedAt: null, updatedBy: null, loading: true });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch(withBase("/api/content/qaida"))
      .then((response) => response.ok ? response.json() as Promise<ApiResponse> : null)
      .then((body) => {
        if (cancelled) return;
        const document = body?.custom ? body.document : null;
        setState({
          // A saved document may legitimately customise only one half — an admin
          // who rewrote the boards but left the pathway alone. Each side falls
          // back to the built-in content on its own.
          lessons: document?.lessons?.length ? document.lessons : BUILT_IN.lessons,
          boards: document?.boards?.length ? document.boards : BUILT_IN.boards,
          custom: Boolean(document),
          updatedAt: body?.updatedAt ?? null,
          updatedBy: body?.updatedBy ?? null,
          loading: false,
        });
      })
      .catch(() => {
        // Offline, or the Vite dev server with no Worker behind it. The built-in
        // content is already on screen; just stop showing a loading state.
        if (!cancelled) setState((current) => ({ ...current, loading: false }));
      });
    return () => { cancelled = true; };
  }, [nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const value = useMemo(() => ({ ...state, reload }), [state, reload]);
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useQaidaContent() {
  return useContext(ContentContext);
}

/** The shipped content, for the admin editor's "start from the defaults" path. */
export const BUILT_IN_CONTENT = BUILT_IN;
